import { useCallback, useEffect, useState } from 'react';
import { collection, onSnapshot } from '../../lib/auditedFirestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { canManageTracker, isCalendarDate } from './trackerModel';

const empty = { datesByQuestion: new Map(), loading: true, slow: false, error: '' };

export function assignmentDates(plans, paperId) {
    const dates = new Map();
    plans.forEach(({ id, ...plan }) => {
        if (!isCalendarDate(id) || (plan.paperId && plan.paperId !== paperId) || !Array.isArray(plan.questionIds)) return;
        new Set(plan.questionIds).forEach(questionId => {
            if (typeof questionId !== 'string' || !questionId) return;
            if (!dates.has(questionId)) dates.set(questionId, []);
            dates.get(questionId).push(id);
        });
    });
    dates.forEach(list => list.sort((a, b) => b.localeCompare(a)));
    return dates;
}

// Mounted only inside the admin plan editor. Reads small daily plans, never IQ content or student progress.
export default function useTrackerAssignments(paperId) {
    const { user, userData } = useAuth();
    const allowed = canManageTracker(userData, paperId);
    const key = `${user?.uid}:${paperId}:${allowed}`;
    const [state, setState] = useState(empty);
    const [revision, setRevision] = useState(0);
    useEffect(() => {
        if (!user?.uid || !allowed) {
            setState({ ...empty, key, loading: false, error: 'You cannot view assignments for this paper.' });
            return;
        }
        let active = true;
        setState({ ...empty, key });
        const timer = setTimeout(() => {
            if (active) setState(previous => previous.loading ? { ...previous, slow: true } : previous);
        }, 10000);
        const stop = onSnapshot(collection(db, 'papers', paperId, 'iqTrackerDays'), { includeMetadataChanges: true }, snapshot => {
            if (!active) return;
            const loading = snapshot.metadata?.fromCache === true;
            if (!loading) clearTimeout(timer);
            setState(previous => ({ key, datesByQuestion: assignmentDates(snapshot.docs.map(d => ({ ...d.data(), id: d.id })), paperId), loading, slow: loading && previous.slow, error: '' }));
        }, () => {
            if (!active) return;
            active = false; clearTimeout(timer);
            setState({ ...empty, key, loading: false, error: 'Earlier assignments could not be checked. Retry before adding questions to avoid accidental repeats.' });
        });
        return () => { active = false; clearTimeout(timer); stop(); };
    }, [user?.uid, paperId, allowed, key, revision]);
    return { ...(state.key === key ? state : empty), reload: useCallback(() => setRevision(value => value + 1), []) };
}
